import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  IconButton,
  CircularProgress,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { es } from 'date-fns/locale';
import {
  formatApiDate,
} from '../../../../utils/dateFormatters-campaign.utils';


export const EditCampaignModal = ({ open, onClose, campaign, onSave }) => {
  const [formData, setFormData] = useState({
    name: '',
    alias: '',
    description: '',
    programming_date: null,
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

 
  useEffect(() => {
    // console.log('Campaign data in EditCampaignModal:', campaign);
    if (open && campaign) {
      setFormData({
        name: campaign.nombre || '',
        alias: campaign.alias || '',
        description: campaign.descripcion || '',
        programming_date: campaign.fechaProgramacion
          ? new Date(campaign.fechaProgramacion)
          : null,
      });
      setErrors({});
    }
  }, [open, campaign]);


  const validate = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'El nombre es requerido';
    }

    if (!formData.alias.trim()) {
      newErrors.alias = 'El alias es requerido';
    }

    if (!formData.programming_date) {
      newErrors.programming_date = 'La fecha de programación es requerida';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (field) => (event) => {
    setFormData((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));

    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: undefined,
      }));
    }
  };


  const handleDateChange = (newValue) => {
    setFormData((prev) => ({
      ...prev,
      programming_date: newValue,
    }));

    if (errors.programming_date) {
      setErrors((prev) => ({
        ...prev,
        programming_date: undefined,
      }));
    }
  };

  const handleSave = async () => {
    if (!validate()) return;

    setLoading(true);

    try {
      const payload = {
        name: formData.name.trim(),
        alias: formData.alias.trim(),
        description: formData.description.trim(),
        programming_date: formatApiDate(formData.programming_date),
      };

      await onSave(payload);
    } catch (error) {
      console.error('Error en handleSave:', error);
    } finally {
      setLoading(false);
    }
  };


  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={loading}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 1,
        }}
      >
         <Typography variant="h3" sx={{py: 2}}>Editar Campaña</Typography>

        <IconButton
          aria-label="cerrar"
          onClick={handleClose}
          disabled={loading}
          sx={{
            color: (theme) => theme.palette.grey[500],
            '&:hover': {
              color: (theme) => theme.palette.grey[700],
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField
            fullWidth
            required
            label="Nombre de campaña"
            value={formData.name}
            onChange={handleChange('name')}
            error={!!errors.name}
            helperText={errors.name}
            disabled={loading}
            size="small"
          />

          <TextField
            fullWidth
            required
            label="Alias"
            value={formData.alias}
            onChange={handleChange('alias')}
            error={!!errors.alias}
            helperText={errors.alias}
            disabled={loading}
            size="small"
          />

          <TextField
            fullWidth
            multiline
            rows={3}
            label="Descripción"
            value={formData.description}
            onChange={handleChange('description')}
            disabled={loading}
            size="small"
            inputProps={{ maxLength: 400 }}
            helperText={`${formData.description.length}/400 caracteres`}
          />

          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
            <DateTimePicker
              label="Fecha y hora de envío"
              value={formData.programming_date}
              onChange={handleDateChange}
              minDate={new Date()}
              disableIgnoringDatePartForTimeValidation
              ampm={false}
              format="dd/MM/yyyy - HH:mm"
              disabled={loading}
              slotProps={{
                textField: {
                  fullWidth: true,
                  required: true,
                  size: 'small',
                  error: !!errors.programming_date,
                  helperText: errors.programming_date || 'Seleccione fecha y hora de envío',
                },
              }}
            />
          </LocalizationProvider>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={handleClose} disabled={loading}>
          Cancelar
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={loading}
          startIcon={loading && <CircularProgress size={20} />}
        >
          {loading ? 'Guardando...' : 'Guardar Cambios'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

EditCampaignModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  campaign: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    alias: PropTypes.string,
    description: PropTypes.string,
    programming_date: PropTypes.string,
  }),
  onSave: PropTypes.func.isRequired,
};